import { MessageEnvelope } from '@zonofi/common/schema/message';
import {
  DateGroupSeparator,
  MessageListBucketMeta,
  MessageListItem,
  MessageListMessage,
  QueryBucketMeta,
  QueryGroupRange,
  QueryGroupSeparator,
} from '../../types';
export const TIMETOKEN_MULTIPLIER = 10_000;

export const mergeAndSortMessages = (
  existingMessages: MessageEnvelope[],
  incomingMessages: MessageEnvelope[]
): MessageEnvelope[] => {
  const messageMap = new Map<string | number, MessageEnvelope>();

  const normalizeMessage = (msg: MessageEnvelope): MessageEnvelope => {
    const displayTimetoken = msg.message?.eventTimestamp
      ? String(msg.message.eventTimestamp)
      : (msg.displayTimetoken ?? msg.timetoken);

    return msg.displayTimetoken === displayTimetoken
      ? msg
      : { ...msg, displayTimetoken };
  };

  existingMessages.forEach((msg) =>
    messageMap.set(msg.timetoken, normalizeMessage(msg))
  );

  incomingMessages.forEach((msg) =>
    messageMap.set(msg.timetoken, normalizeMessage(msg))
  );

  return Array.from(messageMap.values()).sort(
    (a, b) => Number(a.displayTimetoken) - Number(b.displayTimetoken)
  );
};

export const getMessageDisplayTimetoken = (message: MessageEnvelope): number =>
  Number(message.displayTimetoken ?? message.timetoken);

export const getMessageDayKey = (message: MessageEnvelope): string => {
  const ms = getMessageDisplayTimetoken(message) / TIMETOKEN_MULTIPLIER;
  const date = new Date(ms);
  const midnightMs = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();

  return String(midnightMs * TIMETOKEN_MULTIPLIER);
};

export const getMessageBucket = (
  message: MessageEnvelope,
  queryGroupRanges: QueryGroupRange[]
): MessageListBucketMeta => {
  const threadId = message?.message?.conversationThreadId;

  for (const range of queryGroupRanges) {
    if (threadId && threadId === range.conversationThreadId) {
      return {
        type: 'query',
        key: `query-${range.groupId}`,
        groupId: range.groupId,
        lowerBound: range.startTimeToken ?? 0,
        upperBound: range.endTimeToken ?? 0,
        queryType: range.queryType,
        backgroundColor: range.backgroundColor,
      };
    }
  }

  const dayKey = getMessageDayKey(message);
  return {
    type: 'day',
    key: `day-${dayKey}`,
    dayKey,
  };
};

export const createQuerySeparator = (
  bucket: QueryBucketMeta,
  boundary: 'start' | 'end'
): QueryGroupSeparator => ({
  type: 'query',
  boundary,
  groupId: bucket.groupId,
  lowerBound: bucket.lowerBound,
  upperBound: bucket.upperBound,
  queryType: bucket.queryType,
  backgroundColor: bucket.backgroundColor,
});

export const isSameBucket = (
  a: MessageListBucketMeta,
  b: MessageListBucketMeta
): boolean => a.type === 'query' && b.type === 'query' && a.key === b.key;

export const isMessageEnvelopeItem = (
  item: MessageListItem
): item is MessageListMessage => 'message' in item;

export const isDateSeparatorItem = (
  item: MessageListItem
): item is DateGroupSeparator =>
  !('message' in item) && 'type' in item && item.type === 'date';

export const isQuerySeparatorItem = (
  item: MessageListItem
): item is QueryGroupSeparator =>
  !('message' in item) && 'type' in item && item.type === 'query';
